import React, { useState } from 'react'
import {LogIn, Compass, Home, Menu, X, LayoutDashboard, User, Star, Rss, ChevronDown} from 'lucide-react'

interface User {
  userId: string
  userName: string
  email: string
}

interface CommenterUser {
  userId: string
  username: string
  nickname?: string
  profileImageUrl?: string
  subscriptionStatus?: string
}

interface HeaderProps {
  currentView: 'home' | 'explore' | 'projectProfile' | 'dashboard' | 'pricing' | 'userProfile' | 'newsFeed' | 'searchUsers'
  onNavigate: (view: 'home' | 'explore' | 'dashboard' | 'pricing' | 'userProfile' | 'newsFeed' | 'searchUsers') => void
  onViewOwnProfile?: () => void  // New handler to view own profile
  onSignIn: () => void
  onUserLogin: () => void
  user?: User | null
  commenterUser?: CommenterUser | null
}

export function Header({ currentView, onNavigate, onViewOwnProfile, onSignIn, onUserLogin, user, commenterUser }: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false)
  
  return (
    <header className="border-b border-white/10 bg-[#0A0E27]/80 backdrop-blur-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <button 
            onClick={() => onNavigate('home')}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <img 
              src="https://static.lumi.new/e3/e39f2b464939bf84da8bfe117fca0e35.webp" 
              alt="Base Builders Logo" 
              className="w-10 h-10 rounded-lg object-cover"
            />
            <h1 className="text-2xl font-bold text-white">Base Builders</h1>
          </button>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            <button 
              onClick={() => onNavigate('home')}
              className={`flex items-center gap-2 transition-colors ${
                currentView === 'home' ? 'text-white' : 'text-gray-300 hover:text-white'
              }`}
            >
              <Home className="w-4 h-4" />
              Home
            </button>
            <button 
              onClick={() => onNavigate('explore')}
              className={`flex items-center gap-2 transition-colors ${
                currentView === 'explore' || currentView === 'projectProfile' ? 'text-white' : 'text-gray-300 hover:text-white'
              }`}
            >
              <Compass className="w-4 h-4" />
              Explore Projects
            </button>
            <button 
              onClick={() => onNavigate('searchUsers')}
              className={`flex items-center gap-2 transition-colors ${
                currentView === 'searchUsers' ? 'text-white' : 'text-gray-300 hover:text-white'
              }`}
            >
              <User className="w-4 h-4" />
              Search Users
            </button>
            <button 
              onClick={() => onNavigate('newsFeed')}
              className={`flex items-center gap-2 transition-colors ${
                currentView === 'newsFeed' ? 'text-white' : 'text-gray-300 hover:text-white'
              }`}
            >
              <Rss className="w-4 h-4" />
              News Feed
            </button>
            <button 
              onClick={() => onNavigate('pricing')}
              className={`flex items-center gap-2 transition-colors ${
                currentView === 'pricing' ? 'text-white' : 'text-gray-300 hover:text-white'
              }`}
            >
              Pricing
            </button>
            
            {/* Commenter User Button */}
            {commenterUser ? (
              <div className="relative">
                <button 
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                  className="flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 border border-blue-500/30 text-white rounded-lg transition-colors"
                >
                  {commenterUser.profileImageUrl ? (
                    <img src={commenterUser.profileImageUrl} alt="Profile" className="w-5 h-5 rounded-full" />
                  ) : (
                    <User className="w-4 h-4" />
                  )}
                  <span className="text-sm">{commenterUser.nickname || commenterUser.username}</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                
                {/* Dropdown Menu */}
                {profileDropdownOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setProfileDropdownOpen(false)}
                    />
                    <div className="absolute right-0 mt-2 w-48 bg-slate-800 border border-blue-500/30 rounded-lg shadow-xl z-20 overflow-hidden">
                      <button
                        onClick={() => {
                          if (onViewOwnProfile) {
                            onViewOwnProfile()
                          }
                          setProfileDropdownOpen(false)
                        }}
                        className="w-full flex items-center gap-2 px-4 py-3 text-white hover:bg-slate-700 transition-colors text-left"
                      >
                        <User className="w-4 h-4" />
                        View Profile
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <button
                onClick={onUserLogin}
                className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 text-white rounded-lg transition-colors"
              >
                <User className="w-4 h-4" />
                User Login
              </button>
            )}
            
            {/* Project Owner Buttons */}
            {user ? (
              <>
                <button 
                  onClick={() => onNavigate('dashboard')}
                  className={`flex items-center gap-2 transition-colors ${
                    currentView === 'dashboard' ? 'text-white' : 'text-gray-300 hover:text-white'
                  }`}
                >
                  <LayoutDashboard className="w-4 h-4" />
                  Dashboard
                </button>
                <button
                  onClick={() => {
                    onNavigate('dashboard')
                    setTimeout(() => {
                      const section = document.getElementById('feature-project-section')
                      if (section) section.scrollIntoView({ behavior: 'smooth' })
                    }, 100)
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black rounded-lg font-medium hover:opacity-90 transition-opacity"
                >
                  <Star className="w-4 h-4" />
                  Feature Project
                </button>
              </>
            ) : (
              <button
                onClick={onSignIn}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#0052FF] to-[#60A5FA] text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
              >
                <LogIn className="w-4 h-4" />
                List Your Project
              </button>
            )}
          </nav>
          
          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-white p-2"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        
        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/10">
            <nav className="flex flex-col gap-4">
              <button 
                onClick={() => {
                  onNavigate('home')
                  setMobileMenuOpen(false)
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  currentView === 'home' ? 'bg-white/10 text-white' : 'text-gray-300 hover:bg-white/5'
                }`}
              >
                <Home className="w-4 h-4" />
                Home
              </button>
              <button 
                onClick={() => {
                  onNavigate('explore')
                  setMobileMenuOpen(false)
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  currentView === 'explore' || currentView === 'projectProfile' ? 'bg-white/10 text-white' : 'text-gray-300 hover:bg-white/5'
                }`}
              >
                <Compass className="w-4 h-4" />
                Explore Projects
              </button>
              <button 
                onClick={() => {
                  onNavigate('searchUsers')
                  setMobileMenuOpen(false)
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  currentView === 'searchUsers' ? 'bg-white/10 text-white' : 'text-gray-300 hover:bg-white/5'
                }`}
              >
                <User className="w-4 h-4" />
                Search Users
              </button>
              <button 
                onClick={() => {
                  onNavigate('newsFeed')
                  setMobileMenuOpen(false)
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  currentView === 'newsFeed' ? 'bg-white/10 text-white' : 'text-gray-300 hover:bg-white/5'
                }`}
              >
                <Rss className="w-4 h-4" />
                News Feed
              </button>
              <button 
                onClick={() => {
                  onNavigate('pricing')
                  setMobileMenuOpen(false)
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  currentView === 'pricing' ? 'bg-white/10 text-white' : 'text-gray-300 hover:bg-white/5'
                }`}
              >
                Pricing
              </button>
              
              {/* Commenter User Button (Mobile) */}
              {commenterUser ? (
                <>
                  <div className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-blue-500/30 text-white rounded-lg">
                    {commenterUser.profileImageUrl ? (
                      <img src={commenterUser.profileImageUrl} alt="Profile" className="w-5 h-5 rounded-full" />
                    ) : (
                      <User className="w-4 h-4" />
                    )}
                    <span>{commenterUser.nickname || commenterUser.username}</span>
                  </div>
                  <button 
                    onClick={() => {
                      if (onViewOwnProfile) {
                        onViewOwnProfile()
                      }
                      setMobileMenuOpen(false)
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors"
                  >
                    <User className="w-4 h-4" />
                    View Profile
                  </button>
                </>
              ) : (
                <button
                  onClick={() => {
                    onUserLogin()
                    setMobileMenuOpen(false)
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-600 text-white rounded-lg"
                >
                  <User className="w-4 h-4" />
                  User Login
                </button>
              )}
              
              {/* Project Owner Button (Mobile) */}
              {user ? (
                <>
                  <button 
                    onClick={() => {
                      onNavigate('dashboard')
                      setMobileMenuOpen(false)
                    }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                      currentView === 'dashboard' ? 'bg-white/10 text-white' : 'text-gray-300 hover:bg-white/5'
                    }`}
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    Dashboard
                  </button>
                  <button
                    onClick={() => {
                      onNavigate('dashboard')
                      setMobileMenuOpen(false)
                      setTimeout(() => {
                        const section = document.getElementById('feature-project-section')
                        if (section) section.scrollIntoView({ behavior: 'smooth' })
                      }, 100)
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black rounded-lg font-medium hover:opacity-90 transition-opacity"
                  >
                    <Star className="w-4 h-4" />
                    Feature Project
                  </button>
                </>
              ) : (
                <button
                  onClick={() => {
                    onSignIn()
                    setMobileMenuOpen(false)
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#0052FF] to-[#60A5FA] text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
                >
                  <LogIn className="w-4 h-4" />
                  List Your Project
                </button>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  )
}
